import { formatExcelCellValue, numeralFormatter } from 'ns_libs/formatter';
import { IExtractedField } from 'types/db/ExtractedProForma';
import { NOT_LOCATED_VALUE } from 'views/ExtractedProForma/constants';
import { Format, RowValue } from '../../ExecutiveSummaryGenericTable/types';
import { CardItemValue } from '../../layout/cardTypes';

export type ExtractedFieldKey = string;

export type FieldMap = Map<ExtractedFieldKey, IExtractedField[]>;

export type ColumnSuffix = '' | '_trended' | '_at_sale';

export const buildFieldMap = (fields: IExtractedField[]): FieldMap => {
    const map: FieldMap = new Map();
    fields.forEach(field => {
        const matches = map.get(field.key);
        if (matches) matches.push(field);
        else map.set(field.key, [field]);
    });
    return map;
};

export const keysWithPrefix = (fieldMap: FieldMap, prefix: string): ExtractedFieldKey[] => (
    Array.from(fieldMap.keys()).filter(key => key.startsWith(prefix))
);

export const displayValue = (field: IExtractedField): string => (
    field.cannotLocate ? NOT_LOCATED_VALUE : formatExcelCellValue(field.value, field.numberFormat)
);

export const getField = (fieldMap: FieldMap, key: ExtractedFieldKey): IExtractedField | undefined => fieldMap.get(key)?.[0];

/**
 * `id` is the key with its dots swapped out for reactstrap tooltip target so it's not viewed as class delimiter
 */
export const rowId = (key: ExtractedFieldKey): string => key.replace(/\./g, '-');

export const numericValue = (field: IExtractedField | undefined): number | undefined => {
    if (!field || field.cannotLocate) return undefined;
    const numeric = Number(field.value);

    return Number.isNaN(numeric) ? undefined : numeric;
};

export const extractedCell = (fieldMap: FieldMap, key: ExtractedFieldKey) => (): RowValue => {
    const field = getField(fieldMap, key);
    if (!field) return { value: '-', format: 'none' };

    return { value: displayValue(field), format: 'none' };
};

export const derivedCell = (compute: () => number | undefined, format: Format) => (): RowValue => {
    const computed = compute();
    return computed === undefined ? { value: '-', format: 'none' } : { value: computed, format };
};

export const derivedBpsCell = (compute: () => number | undefined) => (): RowValue => {
    const computed = compute();
    return computed === undefined
        ? { value: '-', format: 'none' }
        : { value: numeralFormatter(computed, '0,0'), format: 'none', label: ' bps' };
};

export const notExtracted = () => (): RowValue => ({ value: '-', format: 'none' });

export const notExtractedItem = () => (): CardItemValue | null => null;

export const extractedItem = (fieldMap: FieldMap, key: ExtractedFieldKey) => (): CardItemValue | null => {
    const field = getField(fieldMap, key);
    return field ? { value: displayValue(field) } : null;
};
